import { FC } from 'react';
import { AutoComplete, Popover, Button, Space, Typography, theme } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { StreetAddress, AddressFieldTypeProps } from '#types';
import { useAddressUI } from './useAddressUI';
import DetailedAddressForm from './DetailedAddressForm';
import FlexCol from '../../../atoms/FlexCol';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import type { AddressOption, AddressOptionGroup } from './types';

const { Text } = Typography;

/** What a record row says when its configured property holds no address the field may show. */
const NO_RECORD_ADDRESS_DETAIL = 'No address';

/**
 * Props for the AddressUI component
 */
interface AddressUIProps extends AddressFieldTypeProps {
    /** Current address value (string or structured AddressValue) */
    value?: string | StreetAddress | null | undefined;
    /** Callback when address changes */
    onChange?: (value: StreetAddress) => void;
    /** Whether the input is disabled */
    disabled?: boolean;
    /** Validation status for the input */
    status?: '' | 'error' | 'warning' | undefined;
    /** Additional props passed to AutoComplete */
    [key: string]: any;
}

/**
 * Address input component with Google Maps autocomplete and detailed manual entry form.
 * Provides both quick search and detailed component editing via a popover.
 *
 * One query fills one dropdown: Google's predictions, plus one labelled group per record source the
 * field type configures. `value` stays controlled by the typed text and no `onChange` reaches
 * `AutoComplete`, which is what keeps an option's opaque `value` out of the box; `filterOption` is
 * false because both sides already answered the query.
 */
const AddressUI: FC<AddressUIProps> = ({
    value,
    onChange,
    disabled = false,
    status,
    showRecipient,
    showContact,
    recordSources,
    ...props
}) => {
    const { style, ...restProps } = props;
    const { isPdfExportMode } = usePdfExportContext();
    const {
        addressValue,
        searchValue,
        options,
        isSearching,
        hasRows,
        hasQuery,
        popoverOpen,
        setPopoverOpen,
        handleSearch,
        handleSelect,
        handleComponentChange,
        handleBlur,
        handleClear
    } = useAddressUI({ value, onChange, recordSources, showRecipient, showContact });

    const { token } = theme.useToken();

    return (
        <Space.Compact style={{ width: '100%', ...style }}>
            <AutoComplete<string, AddressOption | AddressOptionGroup>
                {...restProps}
                value={searchValue}
                options={options}
                showSearch={{ onSearch: handleSearch, filterOption: false }}
                onSelect={(selectedValue, option) => {
                    if (option.optionKind !== 'group') void handleSelect(selectedValue, option);
                }}
                optionRender={({ data }) =>
                    data.optionKind === 'record' ? (
                        <FlexCol gap={0}>
                            <Text>{data.label}</Text>
                            {(data.address || data.disabled) && (
                                <Text type="secondary">
                                    {data.address?.formatted ?? NO_RECORD_ADDRESS_DETAIL}
                                </Text>
                            )}
                        </FlexCol>
                    ) : (
                        data.label
                    )
                }
                onClear={handleClear}
                onBlur={handleBlur}
                disabled={disabled}
                status={status}
                allowClear
                placeholder="Search or enter an address..."
                notFoundContent={isSearching && !hasRows ? 'Searching...' : hasQuery && !hasRows ? 'No results found' : null}
                style={{ flex: 1, minWidth: 0, width: '100%' }}
                aria-label="address-autocomplete"
            />
            {!isPdfExportMode && (
                <Popover
                    content={
                        <DetailedAddressForm
                            addressValue={addressValue}
                            disabled={disabled}
                            status={status}
                            onComponentChange={handleComponentChange}
                            showRecipient={showRecipient}
                            showContact={showContact}
                        />
                    }
                    trigger="click"
                    open={popoverOpen}
                    onOpenChange={setPopoverOpen}
                    placement="bottomRight"
                >
                    <Button
                        type="text"
                        style={{ width: token.sizeXL }}
                        icon={<EnvironmentOutlined />}
                        disabled={disabled}
                        aria-label="address-details-button"
                    />
                </Popover>
            )}
        </Space.Compact>
    );
};

export default AddressUI;
